import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Avatar from './Avatar';
import { EVENT_TYPE_ICONS, formatEventType, formatRelative } from './EventCard';

const pluralChanges = (n) => `${n} ${n === 1 ? 'change' : 'changes'}`;

/**
 * Props:
 *   group      — { entityType, entityId, entityName, entityUrl, eventCount, latestAt, userIds: string[], eventTypes: string[] }
 *   usersMap   — { [userId]: { name, avatar } }
 *   isSelected — boolean, highlights the card
 *   onSelect   — (group) => void, called on row click
 */
const EntityGroupCard = ({ group, usersMap, isSelected, onSelect }) => {
  const visibleTypes = group.eventTypes.slice(0, 5);
  const extraTypesCount = group.eventTypes.length - visibleTypes.length;
  const visibleUserIds = group.userIds.slice(0, 3);

  return (
    <div
      onClick={() => onSelect(group)}
      className={`cursor-pointer rounded-lg border p-4 transition-colors ${
        isSelected
          ? 'border-primary-400 dark:border-primary-500 bg-primary-50 dark:bg-primary-900/20'
          : 'border-light-border-primary dark:border-dark-border-primary bg-light-bg-primary dark:bg-dark-bg-primary hover:border-primary-300 dark:hover:border-primary-600 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          {/* Entity type + name */}
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary shrink-0">
              {group.entityType}:
            </span>
            {group.entityUrl ? (
              <Link
                to={group.entityUrl}
                onClick={e => e.stopPropagation()}
                className="font-medium text-sm text-primary-600 dark:text-primary-400 hover:underline truncate max-w-[200px]"
                title={group.entityName}
              >
                {group.entityName}
              </Link>
            ) : (
              <span className="font-medium text-sm text-light-text-primary dark:text-dark-text-primary truncate max-w-[200px]">
                {group.entityName}
              </span>
            )}
          </div>

          {/* Metadata */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="flex items-center gap-0.5">
              {visibleTypes.map(type => (
                <span key={type} title={formatEventType(type)} className="text-sm leading-none">
                  {EVENT_TYPE_ICONS[type] || '📝'}
                </span>
              ))}
              {extraTypesCount > 0 && (
                <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary ml-0.5">
                  +{extraTypesCount}
                </span>
              )}
            </span>
            <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
              {pluralChanges(group.eventCount)}
            </span>
            <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
              · {formatRelative(group.latestAt)}
            </span>
          </div>
        </div>

        {/* User avatars */}
        <div className="flex -space-x-1.5 shrink-0">
          {visibleUserIds.map(uid => {
            const u = usersMap[uid];
            return (
              <div key={uid} className="ring-2 ring-light-bg-primary dark:ring-dark-bg-primary rounded-full">
                <Avatar avatar={u?.avatar ?? null} name={u?.name || uid} size="sm" />
              </div>
            );
          })}
        </div>

        <ChevronRight className={`h-4 w-4 shrink-0 transition-colors ${
          isSelected
            ? 'text-primary-500 dark:text-primary-400'
            : 'text-light-text-tertiary dark:text-dark-text-tertiary'
        }`} />
      </div>
    </div>
  );
};

export default EntityGroupCard;
